import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, ChevronLeft, ArrowLeft } from "lucide-react";
import { useAuth, API } from "@/contexts/AuthContext";

const LoginPage = () => {
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setForgotSent(true);
        return;
      }
      let result;
      if (mode === "login") {
        result = await login(email, password);
      } else {
        result = await register(email, password, companyName);
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      if (mode === "register" || result?.user?.onboarding_completed === false) {
        navigate("/welcome");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === "login";
  const isRegister = mode === "register";
  const isForgot = mode === "forgot";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003858] via-[#006080] to-[#119AB3] flex items-center justify-center p-6 relative overflow-hidden" data-testid="login-page">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#119AB3]/30 rounded-full blur-3xl"></div>
      </div>

      {/* Back to product page link */}
      <button
        data-testid="login-back-btn"
        onClick={() => navigate('/simply-smarter-email')}
        className="absolute top-6 left-6 flex items-center gap-2 text-white/80 hover:text-white transition-colors z-10"
      >
        <ChevronLeft className="w-5 h-5" />
        <span className="font-medium">Back to Simply Smarter Email</span>
      </button>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-afa-lime" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-white leading-tight" style={{fontFamily: 'Manrope'}}>Simply Smarter</span>
              <span className="text-base font-medium text-afa-lime leading-tight">Email</span>
            </div>
          </div>

          {/* Heading */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Manrope'}}>
              {isLogin && "Welcome back"}
              {isRegister && "Create your free account"}
              {isForgot && (forgotSent ? "Check your email" : "Reset your password")}
            </h1>
            <p className="text-white/70 mt-2">
              {isLogin && "Sign in to manage your campaigns"}
              {isRegister && "Sign up in 30 seconds. No credit card."}
              {isForgot && !forgotSent && "Enter your email and we'll send you a reset link."}
              {isForgot && forgotSent && "If that email is in our system, a reset link is on its way."}
            </p>
          </div>

          {/* Forgot password "Email sent" success state */}
          {isForgot && forgotSent ? (
            <div className="space-y-4">
              <div className="bg-afa-lime/10 border border-afa-lime/30 rounded-xl p-4 text-white/90 text-sm leading-relaxed" data-testid="forgot-sent-message">
                We just sent a reset link to <span className="font-semibold text-afa-lime">{email}</span>.
                Check your inbox (and spam folder, just in case). The link works for 1 hour.
              </div>
              <Button
                data-testid="forgot-back-btn"
                onClick={() => { setMode("login"); setForgotSent(false); }}
                className="w-full h-12 bg-afa-lime hover:bg-afa-lime/90 text-afa-navy rounded-full font-semibold text-base"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to sign in
              </Button>
            </div>
          ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label className="text-sm font-medium text-white/90 mb-2 block">Business Name</Label>
                <Input
                  data-testid="company-input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g., Maine Mineral & Gem Museum"
                  className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-afa-lime focus:ring-afa-lime"
                  required={isRegister}
                />
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-white/90 mb-2 block">Email</Label>
              <Input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-afa-lime focus:ring-afa-lime"
                required
                autoComplete={isLogin ? "email" : "off"}
              />
            </div>
            {!isForgot && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium text-white/90 block">Password</Label>
                {isLogin && (
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => setMode("forgot")}
                    className="text-xs text-afa-lime hover:text-white transition-colors font-medium"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-afa-lime focus:ring-afa-lime"
                required={!isForgot}
                autoComplete={isLogin ? "current-password" : "new-password"}
              />
            </div>
            )}
            <Button
              data-testid="submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-afa-lime hover:bg-afa-lime/90 text-afa-navy rounded-full font-semibold text-base shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] mt-6"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                isLogin ? "Sign In" :
                isRegister ? "Create Account" :
                "Send reset link"
              )}
            </Button>
          </form>
          )}

          {/* Toggle */}
          {!isForgot && (
            <p className="text-center mt-6 text-white/70">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                data-testid="toggle-auth-btn"
                onClick={() => setMode(isLogin ? "register" : "login")}
                className="ml-2 text-afa-lime font-semibold hover:text-white transition-colors"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          )}
          {isForgot && !forgotSent && (
            <p className="text-center mt-6 text-white/70">
              <button
                type="button"
                data-testid="forgot-cancel-btn"
                onClick={() => setMode("login")}
                className="text-afa-lime font-semibold hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4 inline mr-1" />Back to sign in
              </button>
            </p>
          )}
        </div>

          <p className="text-center text-white/50 text-sm mt-6">
            Simply Smarter Email · Built by Anne Flinn Advertising
          </p>
      </div>
    </div>
  );
};

export default LoginPage;
